import { StandardTableConfig } from "@stenajs-webui/grid";
import React from "react";
import { Indent, Row, Txt } from "@stenajs-webui/core";
import { RebateOverview } from "@/gql/graphql";
import { IndentValues } from "@/lib/utils/IndentValues";
import { Popover, Tooltip } from "@stenajs-webui/tooltip";
import { formatPeriod, resolveRebateModel } from "../lib/formatter";
import { CellWithTooltip } from "../components/table/CellWithTooltip";
import { EllipsisCell } from "@/components/ellipsis-cell/EllipsisCell";
import { resolvePeriodSortValue } from "../lib/rebateProgressTableUtils";
import {
  ActionMenu,
  ActionMenuItem,
  IconMenuButton,
  stenaDots,
} from "@stenajs-webui/elements";
import { RebateModalMode } from "../components/HandleRebateFormModal";
import { formatAmountWithCurrency } from "../lib/rebateAmountUtils";
import { formatDateRange } from "@/lib/formatters/DateFormatter";
import {
  getActiveGoal,
  getCriteriaNames,
  getCriteriaDescriptions,
  getRebateModelCode,
} from "../lib/rebateOverviewUtils";
import {
  canAuthorizePayment,
  canPayOutOnHigherLevel,
  canPayOutRebate,
  canReleaseRebate,
} from "../lib/rebatePayoutActions";

type RebateColumns =
  | "agreementNumber"
  | "agreementName"
  | "criteria"
  | "description"
  | "goalStatus"
  | "criteriaModel"
  | "period"
  | "target"
  | "rebate"
  | "customers"
  | "sellers"
  | "achievedVolume"
  | "reserved"
  | "paid"
  | "actions";

export const createHandleRebatesTableConfig = (
  onOpenDrawer: (item: RebateOverview) => void,
  onHandleRebate: (item: RebateOverview, mode: RebateModalMode) => void,
  onAuthorize: (item: RebateOverview) => void
): StandardTableConfig<RebateOverview, RebateColumns> => ({
  keyResolver: (item) =>
    `${item.rebateAgreementId}-${item.criteriaIds.join("_")}-${
      item.period
    }-${item.goalIds.join("_")}`,
  disableInfiniteList: true,
  stickyHeader: true,
  columns: {
    agreementNumber: {
      itemValueResolver: (item) =>
        item.rebateAgreement?.rebateAgreementNumber ?? "",
      columnLabel: "No.",
      minWidth: "70px",
    },
    agreementName: {
      itemValueResolver: (item) => item.rebateAgreement?.name ?? "",
      columnLabel: "Agreement",
      minWidth: "180px",
      renderCell: ({ value }) => <EllipsisCell value={value} />,
    },
    criteria: {
      itemValueResolver: (item) => getCriteriaNames(item),
      columnLabel: "Criteria",
      width: "180px",
      minWidth: "180px",
      renderCell: ({ value }) => <EllipsisCell value={value} />,
    },
    description: {
      itemValueResolver: (item) => getCriteriaDescriptions(item),
      columnLabel: "Description",
      minWidth: "150px",
      width: "180px",
      renderCell: ({ value }) => <EllipsisCell value={value} />,
    },
    criteriaModel: {
      itemValueResolver: (item) => getRebateModelCode(item),
      columnLabel: "Model",
      minWidth: "80px",
      renderCell: ({ value }) => {
        const rebateModel = resolveRebateModel(value);
        return (
          <CellWithTooltip
            tooltipLabel={rebateModel.description}
            value={rebateModel.code ?? ""}
          />
        );
      },
    },
    period: {
      itemValueResolver: (item) =>
        resolvePeriodSortValue(item.period, item.rebateTermCode),
      columnLabel: "Period",
      minWidth: "110px",
      renderCell: ({ item }) => {
        const activeGoal = getActiveGoal(item);
        const valueFormatted = formatPeriod(
          item.period,
          item.rebateTermCode,
          activeGoal?.validFrom
        );
        const validFormatted = formatDateRange(
          activeGoal?.validFrom,
          activeGoal?.validTo
        );
        return (
          <CellWithTooltip
            tooltipLabel={validFormatted}
            value={valueFormatted}
          />
        );
      },
    },
    target: {
      itemValueResolver: (item) => item.displayedTarget,
      columnLabel: "Target",
      width: "140px",
      renderCell: ({ item }) => <EllipsisCell value={item.displayedTarget} />,
    },
    rebate: {
      itemValueResolver: (item) =>
        item.goals
          ?.map((goal) => goal.rebateRate)
          .sort((a, b) => a - b)
          .join("/ "),
      columnLabel: "Rebate",
      width: "140px",

      renderCell: ({ value }) => <EllipsisCell value={String(value ?? "")} />,
    },
    achievedVolume: {
      itemValueResolver: (item) => item.achieved,
      columnLabel: "Achieved",
      minWidth: "100px",
    },
    goalStatus: {
      itemValueResolver: (item) => getActiveGoal(item)?.statusCode,
      columnLabel: "Status",
      minWidth: "90px",
    },
    customers: {
      itemValueResolver: (item) =>
        item.customers.map((cust) => cust.custIndex).join(", "),
      columnLabel: "Customers",
      width: "140px",
      renderCell: ({ item }) => {
        const customers = item.customers
          .map((customer) => customer.custIndex)
          .join(", ");
        return <EllipsisCell value={customers} />;
      },
    },
    sellers: {
      itemValueResolver: (item) => item.sellerIds,
      columnLabel: "Sellers",
      width: "120px",
      renderCell: ({ item }) => {
        const sellers =
          item?.sellerIds
            ?.map((s) => s.substring(s.lastIndexOf("\\") + 1))
            .join(", ") ?? "";
        return <EllipsisCell value={sellers} />;
      },
    },
    reserved: {
      itemValueResolver: (item) => item.reservedAmount,
      columnLabel: "Reserved",
      minWidth: "120px",
      renderCell: ({ item }) => (
        <Indent>
          <Txt whiteSpace="nowrap">
            {formatAmountWithCurrency(
              item.reservedAmount,
              item.rebateAgreement?.currencyCode
            )}
          </Txt>
        </Indent>
      ),
    },
    paid: {
      itemValueResolver: (item) => getActiveGoal(item)?.amountPaid,
      columnLabel: "Paid",
      minWidth: "100px",
      renderCell: ({ item, value }) => {
        return (
          <Indent>
            <Txt whiteSpace="nowrap">
              {value
                ? formatAmountWithCurrency(
                    value,
                    item.rebateAgreement?.currencyCode
                  )
                : ""}
            </Txt>
          </Indent>
        );
      },
    },

    actions: {
      itemValueResolver: (item) => "",
      width: "42px",
      columnLabel: "",
      justifyContentCell: "center",
      renderCell: ({ item }) => (
        <Popover
          placement={"left"}
          disablePadding
          trigger={"click"}
          renderTrigger={(props) => (
            <IconMenuButton icon={stenaDots} {...props} />
          )}
        >
          <ActionMenu>
            <ActionMenuItem
              label={"Pay out rebate"}
              disabled={!canPayOutRebate(item)}
              onClick={() => onHandleRebate(item, "PAY")}
            />
            <ActionMenuItem
              label={"Release rebate"}
              disabled={!canReleaseRebate(item)}
              onClick={() => onHandleRebate(item, "RELEASE")}
            />
            <ActionMenuItem
              label={"Authorize payment"}
              disabled={!canAuthorizePayment(item)}
              onClick={() => onAuthorize(item)}
            />
            <ActionMenuItem
              label={"Pay out on a higher level"}
              disabled={!canPayOutOnHigherLevel(item)}
              onClick={() => onHandleRebate(item, "PAYONHIGHERLEVEL")}
            />
            {/* <ActionMenuItem label={"View bookings"} onClick={() => null} />  */}
            <ActionMenuItem
              label={"Show summary"}
              onClick={() => onOpenDrawer(item)}
            />
          </ActionMenu>
        </Popover>
      ),
    },
  },
  columnOrder: [
    "agreementNumber",
    "agreementName",
    "criteria",
    "description",
    "period",
    "criteriaModel",
    "goalStatus",
    "customers",
    "sellers",
    "target",
    "rebate",
    "achievedVolume",
    "reserved",
    "paid",
    "actions",
  ],
});
